import React, { useState, useEffect } from 'react';
import {
  BookOpen, Loader2, CheckCircle, XCircle, Clock,
  MessageSquare, ArrowLeft, Eye, Trash2, User,
  AlertCircle, CornerDownRight, Send,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import api from '../../api';

const STATUS = {
  pending:  { label: 'Pending Review', color: 'bg-amber-100 text-amber-700',     icon: Clock },
  approved: { label: 'Approved',       color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected',       color: 'bg-red-100 text-red-700',         icon: XCircle },
};

export default function AdminLessonNotesPage() {
  const [notes, setNotes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('pending');   // pending | all
  const [selected, setSelected] = useState(null);
  const [loadingNote, setLoadingNote] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [acting, setActing]     = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const pendingCount = notes.filter(n => n.status === 'pending').length;
  const displayed    = tab === 'pending' ? notes.filter(n => n.status === 'pending') : notes;

  useEffect(() => { loadNotes(); }, []);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const r = await api.get('/lesson-notes');
      setNotes(r.data || []);
    } catch { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  };

  const openNote = async (noteId) => {
    setLoadingNote(true);
    setShowRejectForm(false);
    setRejectReason('');
    try {
      const r = await api.get(`/lesson-notes/${noteId}`);
      setSelected(r.data);
    } catch { toast.error('Failed to load note'); }
    finally { setLoadingNote(false); }
  };

  const handleApprove = async () => {
    if (!selected) return;
    setActing(true);
    try {
      await api.put(`/lesson-notes/${selected.id}/approve`, {});
      toast.success('Note approved — students can now access it');
      setSelected(p => ({ ...p, status: 'approved', reject_reason: null }));
      setNotes(prev => prev.map(n => n.id === selected.id ? { ...n, status: 'approved' } : n));
    } catch (err) { toast.error(err?.detail || 'Failed to approve'); }
    finally { setActing(false); }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActing(true);
    try {
      await api.put(`/lesson-notes/${selected.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Note rejected — teacher will be notified');
      setSelected(p => ({ ...p, status: 'rejected', reject_reason: rejectReason.trim() }));
      setNotes(prev => prev.map(n => n.id === selected.id ? { ...n, status: 'rejected' } : n));
      setShowRejectForm(false);
      setRejectReason('');
    } catch (err) { toast.error(err?.detail || 'Failed to reject'); }
    finally { setActing(false); }
  };

  const handleApproveAll = async () => {
    if (!window.confirm(`Approve all ${pendingCount} pending note${pendingCount !== 1 ? 's' : ''}?`)) return;
    setApprovingAll(true);
    try {
      const r = await api.post('/lesson-notes/approve-all', {});
      toast.success(r.message || 'All notes approved');
      setNotes(prev => prev.map(n => n.status === 'pending' ? { ...n, status: 'approved' } : n));
      if (selected?.status === 'pending') setSelected(null);
    } catch (err) { toast.error(err?.detail || 'Failed to approve all'); }
    finally { setApprovingAll(false); }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this lesson note and all its comments?')) return;    try {
      await api.delete(`/lesson-notes/${noteId}`);
      toast.success('Deleted');
      setNotes(prev => prev.filter(n => n.id !== noteId));
      if (selected?.id === noteId) setSelected(null);
    } catch { toast.error('Failed to delete'); }
  };

  const deleteComment = async (commentId) => {
    if (!selected) return;
    try {
      await api.delete(`/lesson-notes/${selected.id}/comments/${commentId}`);
      setSelected(p => ({
        ...p,
        comments: (p.comments || []).filter(c => c.id !== commentId && c.parent_id !== commentId),
      }));
      toast.success('Comment removed');
    } catch { toast.error('Failed to delete comment'); }
  };

  return (
    <div className="flex h-full min-h-[calc(100vh-64px)]" data-testid="admin-lesson-notes-page">

      {/* ── Left: Notes list ── */}
      <div className={`flex flex-col border-r border-slate-200 bg-white flex-shrink-0 w-full lg:w-80 xl:w-96 ${selected ? 'hidden lg:flex' : 'flex'}`}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-base font-bold text-slate-900">Lesson Notes</h1>
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="pending-badge">
                {pendingCount} pending
              </span>
            )}
          </div>
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Total', value: notes.length, color: 'text-slate-700' },
              { label: 'Pending', value: pendingCount, color: 'text-amber-600' },
              { label: 'Approved', value: notes.filter(n => n.status === 'approved').length, color: 'text-emerald-600' },
            ].map(s => (
              <div key={s.label} className="bg-slate-50 rounded-xl p-2.5 text-center">
                <p className={`text-xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3 bg-slate-100 p-1 rounded-xl">
            {[{ key: 'pending', label: 'Review' }, { key: 'all', label: 'All Notes' }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-colors ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.key}`}>
                {t.label}
                {t.key === 'pending' && pendingCount > 0 && (
                  <span className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>
          {/* Approve All — visible in pending tab */}
          {tab === 'pending' && pendingCount > 0 && (
            <button
              onClick={handleApproveAll}
              disabled={approvingAll}
              className="mt-2 w-full flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold py-2 rounded-xl transition-colors"
              data-testid="approve-all-btn"
            >
              {approvingAll
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <CheckCircle className="w-3.5 h-3.5" />
              }
              {approvingAll ? 'Approving...' : `Approve All ${pendingCount} Notes`}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center text-slate-400">
              {tab === 'pending' ? <CheckCircle className="w-10 h-10 mb-3 opacity-30" /> : <BookOpen className="w-10 h-10 mb-3 opacity-30" />}
              <p className="text-sm font-semibold">{tab === 'pending' ? 'All caught up!' : 'No notes yet'}</p>
              <p className="text-xs mt-1">{tab === 'pending' ? 'No notes awaiting review' : "Teachers haven't submitted any notes"}</p>
            </div>
          ) : displayed.map(note => {
            const S = STATUS[note.status] || STATUS.pending;
            const Icon = S.icon;
            return (
              <button key={note.id} onClick={() => openNote(note.id)}
                className={`w-full text-left px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${selected?.id === note.id ? 'bg-blue-50 border-l-2 border-l-[#2563eb]' : ''}`}
                data-testid={`note-item-${note.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-slate-800 leading-tight line-clamp-2">{note.topic}</p>
                  <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${S.color}`}>
                    <Icon className="w-3 h-3" /> {S.label}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{note.subject} · {note.class_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">By {note.teacher_name} · {note.created_at?.slice(0, 10)}</p>
                {note.comment_count > 0 && (
                  <span className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                    <MessageSquare className="w-3 h-3" /> {note.comment_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right: Detail + Actions ── */}
      <div className={`flex-1 flex flex-col overflow-hidden bg-slate-50 ${!selected ? 'hidden lg:flex' : 'flex'}`}>
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <Eye className="w-12 h-12 mb-3 opacity-20" />
            <p className="text-sm font-semibold">Select a note to review</p>
          </div>
        ) : loadingNote ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="w-7 h-7 text-blue-600 animate-spin" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
              <button onClick={() => setSelected(null)}
                className="flex lg:hidden items-center gap-1.5 text-slate-500 text-sm"
                data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> All Notes
              </button>

              {/* Action bar */}
              {selected.status === 'pending' && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="review-bar">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">Awaiting Your Review</p>
                      <p className="text-xs text-amber-600">Approve to publish to students, or reject with a reason</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setShowRejectForm(p => !p); setRejectReason(''); }}
                      className="flex items-center gap-1.5 border border-red-200 text-red-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-red-50 transition-colors"
                      data-testid="reject-btn">
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button onClick={handleApprove} disabled={acting}
                      className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold px-3 py-2 rounded-xl transition-colors"
                      data-testid="approve-btn">
                      {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Approve
                    </button>
                  </div>
                </div>
              )}

              {/* Reject form */}
              {showRejectForm && (
                <div className="bg-white rounded-2xl border border-red-200 p-4 space-y-3">
                  <p className="text-sm font-bold text-red-700">Provide rejection reason</p>
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="e.g. Content needs revision — the objectives are not clearly stated..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-red-200 rounded-xl outline-none focus:border-red-400 resize-none"
                    data-testid="reject-reason-input" />
                  <div className="flex gap-2">
                    <button onClick={() => setShowRejectForm(false)}
                      className="flex-1 border border-slate-200 text-slate-600 text-sm font-semibold py-2 rounded-xl hover:bg-slate-50">
                      Cancel
                    </button>
                    <button onClick={handleReject} disabled={acting || !rejectReason.trim()}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-bold py-2 rounded-xl flex items-center justify-center gap-2"
                      data-testid="confirm-reject-btn">
                      {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Send Rejection
                    </button>
                  </div>
                </div>
              )}

              {/* Approved / Rejected status */}
              {selected.status === 'approved' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between" data-testid="approved-bar">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm font-bold text-emerald-800">Published to Students</p>
                      {selected.approved_at && <p className="text-xs text-emerald-600">Approved on {selected.approved_at?.slice(0, 10)}</p>}
                    </div>
                  </div>
                  <button onClick={() => handleDelete(selected.id)}
                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid="delete-note-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {selected.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between" data-testid="rejected-bar">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-sm font-bold text-red-800">Rejected — awaiting teacher revision</p>
                      <p className="text-xs text-red-600">Reason: {selected.reject_reason}</p>
                    </div>
                  </div>
                  <button onClick={handleApprove} disabled={acting}
                    className="text-xs text-emerald-600 font-bold border border-emerald-300 px-3 py-1.5 rounded-xl hover:bg-emerald-50 transition-colors"
                    data-testid="reconsider-btn">
                    Approve Anyway
                  </button>
                </div>
              )}

              {/* Note info */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{selected.topic}</h2>
                    <p className="text-sm text-slate-500 mt-0.5">{selected.subject} · {selected.class_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">By {selected.teacher_name} · {selected.created_at?.slice(0, 10)}</p>
                  </div>
                  <button onClick={() => handleDelete(selected.id)}
                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    data-testid="delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Full content */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Note Content</p>
                <div className="prose prose-sm max-w-none text-slate-700">
                  <ReactMarkdown>{selected.content || ''}</ReactMarkdown>
                </div>
              </div>

              {/* Comments monitor */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="comments-monitor">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Comments ({(selected.comments || []).length})
                  </p>
                  <span className="text-xs text-slate-400">Admin view — you can delete any comment</span>
                </div>
                <AdminCommentView
                  comments={selected.comments || []}
                  onDelete={deleteComment}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminCommentView({ comments, onDelete }) {
  const topLevel = comments.filter(c => !c.parent_id);
  const replies  = (parentId) => comments.filter(c => c.parent_id === parentId);

  const RoleBadge = ({ role }) => {
    if (role === 'teacher' || role === 'class_teacher')
      return <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Teacher</span>;
    if (role === 'student')
      return <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Student</span>;
    return null;
  };

  if (topLevel.length === 0) return (
    <p className="text-sm text-slate-400 text-center py-6">No comments yet.</p>
  );

  return (
    <div className="space-y-4">
      {topLevel.map(comment => (
        <div key={comment.id}>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1 bg-slate-50 rounded-2xl rounded-tl-none px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-800">{comment.user_name}</p>
                  <RoleBadge role={comment.user_role} />
                  <p className="text-xs text-slate-400">{comment.created_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
                <button onClick={() => onDelete(comment.id)}
                  className="text-slate-300 hover:text-red-500 transition-colors p-1"
                  data-testid={`delete-comment-${comment.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-slate-700 mt-1">{comment.content}</p>
            </div>
          </div>

          {replies(comment.id).map(reply => (
            <div key={reply.id} className="ml-11 mt-2 flex gap-3">
              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div className="flex-1 bg-blue-50 rounded-2xl rounded-tl-none px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-slate-800">{reply.user_name}</p>
                    <RoleBadge role={reply.user_role} />
                    <p className="text-xs text-slate-400">{reply.created_at?.slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  <button onClick={() => onDelete(reply.id)}
                    className="text-slate-300 hover:text-red-500 transition-colors p-1"
                    data-testid={`delete-reply-${reply.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-sm text-slate-700 mt-1">{reply.content}</p>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
